/**
 * Admin Dashboard for Y GO Shuttle Services France
 * Dashboard avec KPIs, CA, missions du jour et réservations récentes
 * Auto-refresh 60s, statuts complets, liens contextuels
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import AdminLayout from '../components/admin/AdminLayout';
import axios from 'axios';
import { 
  Users, Car, Calendar, AlertTriangle, Clock, CheckCircle2,
  TrendingUp, Euro, RefreshCw, ChevronRight, Loader2, MapPin, User, Truck, Bell, Inbox,
  Link2, DollarSign, UserCheck
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import config from 'config';

const API_URL = config.API_URL;

const FULL_STATUS_CONFIG = {
  draft: { label: 'Brouillon', bg: 'bg-gray-500/20', text: 'text-gray-400' },
  quote_sent: { label: 'Devis', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  pending_payment: { label: 'Paiement en attente', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  confirmed: { label: 'Confirmé', bg: 'bg-green-500/20', text: 'text-green-400' },
  pending_assignment: { label: 'À assigner', bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  assigned: { label: 'Assigné', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  driver_assigned: { label: 'Assigné', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  driver_accepted: { label: 'Accepté', bg: 'bg-indigo-500/20', text: 'text-indigo-400' },
  accepted: { label: 'Accepté', bg: 'bg-indigo-500/20', text: 'text-indigo-400' },
  driver_en_route: { label: 'En route', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  en_route: { label: 'En route', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  driver_arrived: { label: 'Sur place', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  on_site: { label: 'Sur place', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  waiting_for_pickup: { label: 'Attente PEC', bg: 'bg-amber-500/20', text: 'text-amber-400' },
  client_on_board: { label: 'Client à bord', bg: 'bg-cyan-500/20', text: 'text-cyan-400' },
  dropped_off: { label: 'Client déposé', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  in_progress: { label: 'En cours', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  completed: { label: 'Terminé', bg: 'bg-green-500/20', text: 'text-green-400' },
  cancelled: { label: 'Annulé', bg: 'bg-red-500/20', text: 'text-red-400' },
  closed: { label: 'Clos', bg: 'bg-green-500/20', text: 'text-green-400' },
  verified: { label: 'Vérifié', bg: 'bg-teal-500/20', text: 'text-teal-400' },
  invoiced: { label: 'Facturé', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
  no_show: { label: 'Client absent', bg: 'bg-red-800/20', text: 'text-red-400' },
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [adminStats, setAdminStats] = useState(null);
  const [todayMissions, setTodayMissions] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  const intervalRef = useRef(null);

  const fetchDashboardData = useCallback(async () => {
    setRefreshing(true);
    const token = localStorage.getItem('access_token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [statsRes, missionsRes, adminDashRes] = await Promise.all([
        axios.get(`${API_URL}/api/planning/missions/stats`, { headers }),
        axios.get(`${API_URL}/api/planning/missions-today`, { headers }),
        axios.get(`${API_URL}/api/admin/dashboard`, { headers }).catch(() => ({ data: {} }))
      ]);
      setStats(statsRes.data);
      setTodayMissions(missionsRes.data.missions || []);
      setRecentBookings(missionsRes.data.recent_bookings || []);
      setAdminStats(adminDashRes.data);
      setLastRefreshed(new Date());
    } catch (err) {
      console.error('Failed to fetch dashboard data:', err);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();
    intervalRef.current = setInterval(fetchDashboardData, 60000);
    return () => clearInterval(intervalRef.current);
  }, [fetchDashboardData]);

  const getStatusBadge = (status, isNew = false) => {
    const config = FULL_STATUS_CONFIG[status] || FULL_STATUS_CONFIG.draft;
    return (
      <span className="flex items-center gap-1">
        {isNew && <span className="w-2 h-2 bg-[#C9A24D] rounded-full animate-pulse" title="Nouvelle réservation" />}
        <span className={`px-2 py-0.5 rounded text-xs ${config.bg} ${config.text}`}>{config.label}</span>
      </span>
    );
  };

  const todayStr = format(new Date(), 'yyyy-MM-dd');

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-[80vh]">
          <Loader2 className="w-8 h-8 text-[#C9A24D] animate-spin" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-6" data-testid="admin-dashboard">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-[#E5E5E5]" data-testid="dashboard-title">Tableau de bord</h1>
            <p className="text-[#E5E5E5]/60 text-sm">{format(new Date(), "EEEE d MMMM yyyy", { locale: fr })}</p>
          </div>
          <div className="flex items-center gap-3">
            {lastRefreshed && (
              <span className="text-[#E5E5E5]/40 text-xs hidden sm:inline" data-testid="last-updated">
                MAJ {format(lastRefreshed, 'HH:mm:ss')}
              </span>
            )}
            <Button onClick={fetchDashboardData} disabled={refreshing} variant="outline" className="border-[#C9A24D]/30 text-[#E5E5E5]" data-testid="refresh-btn">
              <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} /> Actualiser
            </Button>
          </div>
        </div>

        {/* Alerts */}
        {stats && (stats.pending_assignment > 0 || recentBookings.length > 0) && (
          <div className="mb-6 space-y-2">
            {stats.pending_assignment > 0 && (
              <div className="flex items-center gap-3 p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg" data-testid="alert-pending-assignment">
                <AlertTriangle className="w-5 h-5 text-yellow-400" />
                <span className="text-yellow-400 text-sm">{stats.pending_assignment} mission(s) en attente d&apos;assignation</span>
                <Link to={`/admin/planning?date=${todayStr}`} className="ml-auto text-yellow-400 text-sm hover:underline">Voir &rarr;</Link>
              </div>
            )}
            {recentBookings.length > 0 && (
              <div className="flex items-center gap-3 p-3 bg-[#C9A24D]/10 border border-[#C9A24D]/30 rounded-lg" data-testid="alert-new-bookings">
                <Bell className="w-5 h-5 text-[#C9A24D]" />
                <span className="text-[#C9A24D] text-sm">{recentBookings.length} nouvelle(s) réservation(s) dans les dernières 24h</span>
                <Link to={`/admin/planning?date=${todayStr}`} className="ml-auto text-[#C9A24D] text-sm hover:underline">Traiter &rarr;</Link>
              </div>
            )}
          </div>
        )}

        {/* Dispatch shortcut */}
        <Link to="/admin/dispatch" data-testid="dispatch-shortcut">
          <div className="mb-6 flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-lg hover:bg-emerald-500/15 transition cursor-pointer">
            <MapPin className="w-5 h-5 text-emerald-400" />
            <span className="text-emerald-400 text-sm font-medium">Dispatch en direct &mdash; Carte temps réel</span>
            <ChevronRight className="w-4 h-4 text-emerald-400 ml-auto" />
          </div>
        </Link>

        {/* KPI Cards - Row 1: Missions */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-4" data-testid="kpi-missions">
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/60 text-xs">Missions aujourd&apos;hui</p>
                  <p className="text-2xl font-bold text-[#E5E5E5]" data-testid="kpi-missions-today">{stats?.missions_today || 0}</p>
                </div>
                <Calendar className="w-8 h-8 text-[#C9A24D]/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/60 text-xs">En cours</p>
                  <p className="text-2xl font-bold text-[#C9A24D]" data-testid="kpi-in-progress">{(stats?.in_progress || 0) + (stats?.drivers_en_route || 0)}</p>
                </div>
                <TrendingUp className="w-8 h-8 text-[#C9A24D]/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/60 text-xs">Terminées aujourd&apos;hui</p>
                  <p className="text-2xl font-bold text-green-500" data-testid="kpi-completed">{stats?.completed_today || 0}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 text-green-500/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/60 text-xs">A assigner</p>
                  <p className="text-2xl font-bold text-yellow-400" data-testid="kpi-pending-assignment">{stats?.pending_assignment || 0}</p>
                </div>
                <AlertTriangle className="w-8 h-8 text-yellow-400/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/60 text-xs">En attente client</p>
                  <p className="text-2xl font-bold text-blue-400" data-testid="kpi-pending-client">{stats?.pending_client || 0}</p>
                </div>
                <Clock className="w-8 h-8 text-blue-400/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/10">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/60 text-xs">A facturer</p>
                  <p className="text-2xl font-bold text-amber-400" data-testid="kpi-to-invoice">{stats?.to_invoice || 0}</p>
                </div>
                <Euro className="w-8 h-8 text-amber-400/40" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* KPI Cards - Row 2: Business (CA + Team) */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="kpi-business">
          <Card className="bg-[#1F1F1F] border-emerald-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/60 text-xs">CA du jour</p>
                  <p className="text-2xl font-bold text-emerald-400" data-testid="kpi-revenue-today">
                    {(adminStats?.total_revenue_today || 0).toLocaleString('fr-FR')} &euro;
                  </p>
                </div>
                <DollarSign className="w-8 h-8 text-emerald-400/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1F1F1F] border-emerald-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/60 text-xs">CA du mois</p>
                  <p className="text-2xl font-bold text-emerald-400" data-testid="kpi-revenue-month">
                    {(adminStats?.total_revenue_month || 0).toLocaleString('fr-FR')} &euro;
                  </p>
                </div>
                <Euro className="w-8 h-8 text-emerald-400/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1F1F1F] border-blue-500/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[#E5E5E5]/60 text-xs">Chauffeurs actifs</p>
                  <p className="text-2xl font-bold text-blue-400" data-testid="kpi-active-drivers">
                    {adminStats?.active_chauffeurs || 0}
                    <span className="text-sm font-normal text-[#E5E5E5]/40"> / {adminStats?.total_chauffeurs || 0}</span>
                  </p>
                </div>
                <UserCheck className="w-8 h-8 text-blue-400/40" />
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#1F1F1F] border-purple-500/20 cursor-pointer hover:border-purple-500/40 transition-colors" onClick={() => navigate('/admin/clients')} data-testid="kpi-clients-card">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-[#E5E5E5]/60 text-xs">Clients</p>
                  <p className="text-2xl font-bold text-purple-400" data-testid="kpi-total-clients">
                    {adminStats?.total_clients || 0}
                    <span className="text-xs font-normal text-[#E5E5E5]/50"> fiches</span>
                  </p>
                  <div className="text-[11px] text-[#E5E5E5]/50 mt-1 flex gap-3">
                    <span title="Prospects issus des bookings guests (db.clients)" data-testid="kpi-crm-leads">
                      <span className="text-[#C9A24D]">{adminStats?.crm_leads || 0}</span> prospects
                    </span>
                    <span title="Clients disposant d'un compte connecté" data-testid="kpi-clients-with-account">
                      <span className="text-emerald-400">{adminStats?.clients_with_account || 0}</span> comptes
                    </span>
                  </div>
                </div>
                <Users className="w-8 h-8 text-purple-400/40 shrink-0" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Two Column Layout for Missions and Recent Bookings */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          
          {/* Widget 1: Missions du jour */}
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
            <div className="p-4 border-b border-[#C9A24D]/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Calendar className="w-5 h-5 text-[#C9A24D]" />
                <h2 className="text-lg font-semibold text-[#E5E5E5]">Missions du jour</h2>
                <span className="text-xs text-[#E5E5E5]/50 bg-[#C9A24D]/10 px-2 py-0.5 rounded">{todayMissions.length}</span>
              </div>
              <Link to={`/admin/planning?date=${todayStr}`} className="text-[#C9A24D] text-sm hover:underline flex items-center gap-1" data-testid="see-all-missions">
                Voir tout <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <CardContent className="p-0 max-h-[450px] overflow-y-auto">
              {todayMissions.length === 0 ? (
                <div className="p-8 text-center text-[#E5E5E5]/50">
                  <Calendar className="w-10 h-10 mx-auto mb-3 text-[#E5E5E5]/20" />
                  <p>Aucune mission programmée aujourd&apos;hui</p>
                </div>
              ) : (
                <div className="divide-y divide-[#C9A24D]/10">
                  {todayMissions.slice(0, 10).map((mission) => (
                    <Link
                      key={mission.id}
                      to={`/admin/planning?date=${todayStr}`}
                      className={`block p-3 hover:bg-[#C9A24D]/5 transition-colors ${mission.cancellation_requested ? 'border-l-2 border-red-500 bg-red-500/5' : ''}`}
                      data-testid={`mission-card-${mission.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-center min-w-[50px]">
                          <p className="text-[#C9A24D] font-bold">
                            {mission.pickup_datetime ? format(parseISO(mission.pickup_datetime), 'HH:mm') : '--:--'}
                          </p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <span className="text-[#E5E5E5] font-medium text-sm truncate">
                              {mission.client_name || 'Client'}
                            </span>
                            {getStatusBadge(mission.status)}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-[#E5E5E5]/50">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            <span className="truncate">{mission.pickup_address?.split(',')[0]}</span>
                            <span className="text-[#C9A24D]">&rarr;</span>
                            <span className="truncate">{mission.dropoff_address?.split(',')[0]}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {mission.driver_name ? (
                            <span className="text-xs text-green-400 flex items-center gap-1">
                              <Car className="w-3 h-3" />
                              {mission.driver_name}
                            </span>
                          ) : (
                            <span className="text-xs text-yellow-400">Non assign&eacute;</span>
                          )}
                          {mission.total_price > 0 && (
                            <p className="text-[#C9A24D] text-xs">{mission.total_price.toFixed(0)}&euro;</p>
                          )}
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Widget 2: Réservations récentes (dernières 24h) */}
          <Card className="bg-[#1F1F1F] border-[#C9A24D]/20">
            <div className="p-4 border-b border-[#C9A24D]/20 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Inbox className="w-5 h-5 text-[#C9A24D]" />
                <h2 className="text-lg font-semibold text-[#E5E5E5]">Nouvelles réservations</h2>
                {recentBookings.length > 0 && (
                  <span className="text-xs text-[#0B0B0B] bg-[#C9A24D] px-2 py-0.5 rounded-full font-bold animate-pulse">
                    {recentBookings.length}
                  </span>
                )}
              </div>
              <span className="text-xs text-[#E5E5E5]/40">Dernières 24h</span>
            </div>
            <CardContent className="p-0 max-h-[450px] overflow-y-auto">
              {recentBookings.length === 0 ? (
                <div className="p-8 text-center text-[#E5E5E5]/50">
                  <CheckCircle2 className="w-10 h-10 mx-auto mb-3 text-green-500/30" />
                  <p>Toutes les réservations ont été traitées</p>
                </div>
              ) : (
                <div className="divide-y divide-[#C9A24D]/10">
                  {recentBookings.slice(0, 10).map((booking) => (
                    <Link
                      key={booking.id}
                      to={`/admin/planning?date=${booking.pickup_datetime ? format(parseISO(booking.pickup_datetime), 'yyyy-MM-dd') : todayStr}`}
                      className="block p-3 hover:bg-[#C9A24D]/5 border-l-2 border-[#C9A24D] transition-colors"
                      data-testid={`booking-card-${booking.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="text-center min-w-[50px]">
                          <span className="w-2 h-2 bg-[#C9A24D] rounded-full inline-block animate-pulse mb-1" />
                          <p className="text-[#C9A24D] text-xs font-mono">
                            {booking.reference?.slice(-8) || booking.id?.slice(-8).toUpperCase()}
                          </p>
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <User className="w-3 h-3 text-[#E5E5E5]/40" />
                            <span className="text-[#E5E5E5] font-medium text-sm truncate">
                              {booking.client_name || 'Client'}
                            </span>
                            {getStatusBadge(booking.status, true)}
                          </div>
                          <div className="flex items-center gap-1 text-xs text-[#E5E5E5]/50">
                            <Clock className="w-3 h-3" />
                            <span>
                              {booking.pickup_datetime 
                                ? format(parseISO(booking.pickup_datetime), "dd/MM HH:mm")
                                : 'Date non définie'}
                            </span>
                            <span className="mx-1">&bull;</span>
                            <span className="truncate">{booking.pickup_address?.split(',')[0]}</span>
                          </div>
                        </div>
                        <div className="text-right flex-shrink-0">
                          {booking.total_price > 0 && (
                            <p className="text-[#C9A24D] font-bold">{booking.total_price.toFixed(0)}&euro;</p>
                          )}
                          <span className="text-xs text-[#C9A24D]">Traiter &rarr;</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Quick Links */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4" data-testid="quick-links">
          <Link to="/admin/planning" className="p-4 bg-[#1F1F1F] border border-[#C9A24D]/20 rounded-lg hover:border-[#C9A24D]/50 transition-colors" data-testid="quick-link-planning">
            <Calendar className="w-6 h-6 text-[#C9A24D] mb-2" />
            <p className="text-[#E5E5E5] font-medium">Planning</p>
            <p className="text-[#E5E5E5]/50 text-xs">Gérer les missions</p>
          </Link>
          <Link to="/admin/tracking-links" className="p-4 bg-[#1F1F1F] border border-[#C9A24D]/20 rounded-lg hover:border-[#C9A24D]/50 transition-colors" data-testid="quick-link-tracking">
            <Link2 className="w-6 h-6 text-[#C9A24D] mb-2" />
            <p className="text-[#E5E5E5] font-medium">Tracking</p>
            <p className="text-[#E5E5E5]/50 text-xs">Liens de suivi</p>
          </Link>
          <Link to="/admin/vehicles" className="p-4 bg-[#1F1F1F] border border-[#C9A24D]/20 rounded-lg hover:border-[#C9A24D]/50 transition-colors" data-testid="quick-link-vehicles">
            <Truck className="w-6 h-6 text-[#C9A24D] mb-2" />
            <p className="text-[#E5E5E5] font-medium">Véhicules</p>
            <p className="text-[#E5E5E5]/50 text-xs">Gérer la flotte</p>
          </Link>
          <Link to="/admin/drivers" className="p-4 bg-[#1F1F1F] border border-[#C9A24D]/20 rounded-lg hover:border-[#C9A24D]/50 transition-colors" data-testid="quick-link-drivers">
            <Users className="w-6 h-6 text-[#C9A24D] mb-2" />
            <p className="text-[#E5E5E5] font-medium">Chauffeurs</p>
            <p className="text-[#E5E5E5]/50 text-xs">Equipe conducteurs</p>
          </Link>
          <Link to="/admin/statistics" className="p-4 bg-[#1F1F1F] border border-[#C9A24D]/20 rounded-lg hover:border-[#C9A24D]/50 transition-colors" data-testid="quick-link-stats">
            <TrendingUp className="w-6 h-6 text-[#C9A24D] mb-2" />
            <p className="text-[#E5E5E5] font-medium">Statistiques</p>
            <p className="text-[#E5E5E5]/50 text-xs">CA et analyses</p>
          </Link>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
